import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalPortal from './ModalPortal';
import { CloseIcon, InfoCircleIcon, LockOutlineIcon } from './wizardIcons';

const MANIFESTO_KEYS = [
  'wizard.privacyStep.point1',
  'wizard.privacyStep.point2',
  'wizard.privacyStep.point3',
  'wizard.privacyStep.point4',
  'wizard.privacyStep.point5',
];

/** Each right has `wizard.privacyStep.rights.<id>Title` / `<id>Body`. */
const RIGHT_IDS = [
  'memory',
  'conversation',
  'dataUse',
  'know',
  'correct',
  'delete',
  'portability',
  'limit',
  'access',
  'providers',
  'security',
];

interface PrivacyRightsModalProps {
  onClose: () => void;
  onContinue: () => void;
}

/** "Your privacy, in plain English" — the full list of rights. Scrolls
 * within itself; the close button and the continue CTA stay put. */
const PrivacyRightsModal: React.FC<PrivacyRightsModalProps> = ({
  onClose,
  onContinue,
}) => {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const { overflow: previousOverflow, paddingRight: previousPaddingRight } =
      document.body.style;
    // Compensate for the scrollbar disappearing so the page doesn't shift.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <ModalPortal>
      <div
        className='privacy-backdrop'
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className='privacy-modal'
          role='dialog'
          aria-modal='true'
          aria-labelledby='privacy-modal-title'
        >
          <div className='privacy-modal-head'>
            <button
              ref={closeRef}
              type='button'
              className='privacy-modal-close'
              aria-label={t('wizard.close')}
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>
          <div className='privacy-modal-scroll'>
            <div className='privacy-modal-lock'>
              <LockOutlineIcon />
            </div>
            <h2 id='privacy-modal-title'>
              {t('wizard.privacyStep.modalTitle')}
            </h2>
            <ol className='privacy-rights'>
              {RIGHT_IDS.map((id, i) => (
                <li key={id}>
                  <h3>
                    {i + 1}. {t(`wizard.privacyStep.rights.${id}Title`)}
                  </h3>
                  <p>{t(`wizard.privacyStep.rights.${id}Body`)}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className='privacy-modal-foot'>
            <button type='button' className='btn-primary' onClick={onContinue}>
              <span>{t('wizard.getStarted')}</span>
              <svg
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.75'
                aria-hidden='true'
              >
                <path
                  d='M3 8h10M9 4l4 4-4 4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

interface PrivacyStepProps {
  eyebrow: string;
  title?: string;
  /** Advances the wizard (same as the footer CTA). */
  onContinue: () => void;
  /** "Help make your assistant better" — on by default, persisted on continue. */
  analyticsConsent: boolean;
  onAnalyticsConsentChange: (enabled: boolean) => void;
}

/** Step 1: the privacy manifesto, first thing after the WhatsApp link. */
const PrivacyStep: React.FC<PrivacyStepProps> = ({
  eyebrow,
  title,
  onContinue,
  analyticsConsent,
  onAnalyticsConsentChange,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  return (
    <section className='screen active' data-screen='privacy'>
      <div className='eyebrow'>{eyebrow}</div>
      <h1 className='ob-title'>{title ?? t('wizard.privacyStep.title')}</h1>
      <p className='ob-lede'>{t('wizard.privacyStep.lede')}</p>

      <div className='manifesto'>
        <div className='manifesto-lock'>
          <LockOutlineIcon />
        </div>
        <ol className='manifesto-list'>
          {MANIFESTO_KEYS.map((key, i) => (
            <li key={key}>
              <span className='manifesto-num'>{i + 1}.</span>
              <span>{t(key)}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className='privacy-choice'>
        <div className='row-text'>
          <div className='row-title'>
            {t('wizard.privacyStep.analyticsTitle')}
          </div>
          <div className='row-sub'>{t('wizard.privacyStep.analyticsBody')}</div>
        </div>
        <button
          type='button'
          className='toggle'
          role='switch'
          aria-checked={analyticsConsent}
          aria-label={t('wizard.privacyStep.analyticsTitle')}
          onClick={() => onAnalyticsConsentChange(!analyticsConsent)}
        ></button>
      </div>

      <div className='privacy-more'>
        <button
          type='button'
          className='privacy-more-btn'
          onClick={() => setModalOpen(true)}
        >
          <InfoCircleIcon />
          {t('wizard.privacyStep.learnMore')}
        </button>
      </div>

      {modalOpen && (
        <PrivacyRightsModal
          onClose={closeModal}
          onContinue={() => {
            setModalOpen(false);
            onContinue();
          }}
        />
      )}
    </section>
  );
};

export default PrivacyStep;
